/* eslint-disable no-undef */
import * as Sentry from '@sentry/react';
import React from 'react';
import { createRoot } from 'react-dom/client';
import {
  BrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType
} from 'react-router-dom';
import { App } from './app/App';
import { ErrorBoundary } from './app/components/ErrorBoundary';
import { sanitizeTelemetryRecord } from '@cindro/client';
import cssText from './styles.css?raw';

// Glitchtip DSN, e.g. https://<public_key>@errors.nerds.cooking/<project_id>.
// Baked in at build time — this app is Cindro's own config source, so it
// can't bootstrap error reporting from a runtime-config fetch the way
// consuming apps (skillora etc.) do. Empty/unset disables the client.
const telemetryEnvironment = window.location.hostname.startsWith('staging.')
  ? 'staging'
  : window.location.hostname === 'localhost'
    ? 'development'
    : 'production';

Sentry.init({
  environment: telemetryEnvironment,
  release: process.env.NX_PUBLIC_COMMIT_HASH,
  dsn: process.env.NX_PUBLIC_GLITCHTIP_DSN,
  enableLogs: true,
  integrations: [
    // Console/network breadcrumbs so a captured error carries recent
    // app activity, not just the error itself.
    Sentry.breadcrumbsIntegration({
      console: true,
      dom: true,
      fetch: true,
      xhr: true,
      history: true
    }),
    // Glitchtip does implement Sentry's performance/transactions API
    // (glitchtip.com/documentation/performance) — Session Replay genuinely
    // isn't supported, so that integration stays out.
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes
    }),
    Sentry.consoleLoggingIntegration({ levels: ['info', 'warn', 'error'] })
  ],
  maxBreadcrumbs: 150,
  tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,
  beforeSend: (event) => sanitizeTelemetryRecord(event),
  beforeSendLog: (log) => sanitizeTelemetryRecord(log)
  // No autoSessionTracking option as of Sentry SDK v9+ (removed) — session
  // tracking is now purely integration-driven, and we never add
  // browserSessionIntegration, so sessions are never sent. GlitchTip
  // doesn't support Sentry's session concept anyway.
});

function injectStyles() {
  const existing = document.querySelector<HTMLStyleElement>(
    'style[data-cindro-admin-styles]'
  );

  if (existing) {
    existing.textContent = cssText;
    return;
  }

  const style = document.createElement('style');
  style.dataset['cindroAdminStyles'] = 'true';
  style.textContent = cssText;
  document.head.appendChild(style);
}

async function bootstrap() {
  injectStyles();

  const root = createRoot(document.getElementById('root') as HTMLElement);
  root.render(
    <React.StrictMode>
      <ErrorBoundary>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </ErrorBoundary>
    </React.StrictMode>
  );
}

void bootstrap();
